import { useEffect, useRef } from "react";
import { ArrowLeft, Sparkles, UsersRound } from "lucide-react";
import { useUiLanguage } from "./i18n/UiLanguageContext";

export type DemoParticipantExperience = "collaboration" | "liveQuiz";

export default function DemoParticipantLimitModal({
  experience,
  onBackToHub,
  onStartTrial,
}: {
  experience: DemoParticipantExperience;
  onBackToHub: () => void;
  onStartTrial: () => void;
}) {
  const { t } = useUiLanguage();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const detailKey = experience === "collaboration"
    ? "demo.participantLimit.collaborationDetail"
    : "demo.participantLimit.liveQuizDetail";

  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onBackToHub();
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const controls = Array.from(dialog.querySelectorAll<HTMLElement>("button:not([disabled]), [href], input:not([disabled])"));
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBackToHub]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4" role="presentation">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-participant-limit-title"
        aria-describedby="demo-participant-limit-description"
        className="w-full max-w-lg rounded-[28px] border border-violet-100 bg-white p-6 shadow-2xl outline-none sm:p-7"
      >
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white shadow-lg">
            <UsersRound aria-hidden="true" size={25} />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-700">Elume demo</p>
            <h1 id="demo-participant-limit-title" className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              {t("demo.participantLimit.title")}
            </h1>
          </div>
        </div>

        <p id="demo-participant-limit-description" className="mt-5 text-base leading-7 text-slate-700">
          {t("demo.participantLimit.inUse")}
        </p>
        <p className="mt-4 rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm leading-6 text-slate-700">
          {t(detailKey)}
        </p>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onBackToHub} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-bold text-slate-800 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-violet-100">
            <ArrowLeft aria-hidden="true" size={18} />
            {t("demo.participantLimit.backToHub")}
          </button>
          <button type="button" onClick={onStartTrial} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-500 px-4 py-2.5 font-bold text-white shadow-sm transition hover:brightness-105 focus:outline-none focus:ring-4 focus:ring-emerald-100">
            <Sparkles aria-hidden="true" size={18} />
            {t("demo.participantLimit.startTrial")}
          </button>
        </div>
      </div>
    </div>
  );
}
